import React, { useEffect, useRef, useState } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  useLocation,
  useNavigate,
} from "react-router-dom";
import Header from "./components/Header/Header";
import Hero from "./components/Hero/Hero";
import Compat from "./components/Compat/Compat";
import Pipeline from "./components/Pipeline/Pipeline";
import Intelligence from "./components/Intelligence/Intelligence";
import About from "./components/About/About";
import Services from "./components/Services/Services";
import Deployment from "./components/Deployment/Deployment";
import UseCases from "./components/UseCases/UseCases";
import Contact from "./components/Contact/Contact";
import Footer from "./components/Footer/Footer";
import VisionPlatform from "./components/VisionPlatform/VisionPlatform";
import BuildSolutions from "./components/BuildSolutions/BuildSolutions";
import EquipIntelligence from "./components/EquipIntelligence/EquipIntelligence";
import ErrorPage from "./components/ErrorPage/ErrorPage";
import {
  scrollToSection,
  LANDING_SECTIONS,
  LANDING_PATHS,
  HEADER_OFFSET,
} from "./lib/scroll";
import { useSeo } from "./lib/seo";

/** Normalises "/about/" to "/about". */
function TrailingSlashHandler() {
  const location = useLocation();
  const navigate = useNavigate();
  useEffect(() => {
    if (location.pathname.endsWith("/") && location.pathname !== "/") {
      navigate(
        location.pathname.replace(/\/+$/, "") + location.search + location.hash,
        { replace: true },
      );
    }
  }, [location, navigate]);
  return null;
}

/**
 * On a fresh load of /about, /services… scroll to that section.
 * Navigations triggered by the header or by scroll-sync are ignored.
 */
function ScrollHandler() {
  const location = useLocation();
  useEffect(() => {
    if (location.state?.fromScrollSync || location.state?.fromHeaderClick) return;
    if (!LANDING_PATHS.includes(location.pathname)) {
      window.scrollTo({ top: 0, behavior: "auto" });
      return;
    }
    const section = location.pathname === "/" ? "home" : location.pathname.slice(1);
    const t = setTimeout(() => scrollToSection(section), 60);
    return () => clearTimeout(t);
  }, [location.pathname, location.state]);
  return null;
}

/** Keeps the URL in sync with the section closest to the top of the viewport. */
function URLSyncOnScroll({ scrollingManually }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [ready, setReady] = useState(false);
  const ticking = useRef(false);

  useEffect(() => {
    const t = setTimeout(() => setReady(true), 600);
    return () => clearTimeout(t);
  }, []);

  useEffect(() => {
    if (!ready || scrollingManually || !LANDING_PATHS.includes(location.pathname)) return;

    const update = () => {
      ticking.current = false;
      let closest = null;
      let min = Infinity;
      LANDING_SECTIONS.forEach((id) => {
        const el = document.getElementById(id);
        if (!el) return;
        const d = Math.abs(el.getBoundingClientRect().top - HEADER_OFFSET);
        if (d < min) {
          min = d;
          closest = id;
        }
      });
      if (!closest) return;
      const path = closest === "home" ? "/" : `/${closest}`;
      if (location.pathname !== path) {
        navigate(path, { replace: true, state: { fromScrollSync: true } });
      }
    };

    const onScroll = () => {
      if (ticking.current) return;
      ticking.current = true;
      window.requestAnimationFrame(update);
    };

    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, [ready, scrollingManually, location.pathname, navigate]);

  return null;
}

function Landing() {
  useSeo({ path: "/" });
  return (
    <>
      <Hero />
      <Compat />
      <Pipeline />
      <Intelligence />
      <About />
      <Services />
      <Deployment />
      <UseCases />
      <Contact />
    </>
  );
}

export default function App() {
  const [scrollingManually, setScrollingManually] = useState(false);

  return (
    <Router>
      <TrailingSlashHandler />
      <ScrollHandler />
      <URLSyncOnScroll scrollingManually={scrollingManually} />
      <Header setScrollingManually={setScrollingManually} />
      <main>
        <Routes>
          {LANDING_PATHS.map((p) => (
            <Route key={p} path={p} element={<Landing />} />
          ))}
          <Route path="/vision-platform" element={<VisionPlatform />} />
          <Route path="/build-solutions" element={<BuildSolutions />} />
          <Route path="/equip-intelligence" element={<EquipIntelligence />} />
          <Route path="*" element={<ErrorPage />} />
        </Routes>
      </main>
      <Footer />
    </Router>
  );
}
